import React from 'react';
import CodeMirror, { dropCursor } from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { json } from '@codemirror/lang-json';
import { markdown } from '@codemirror/lang-markdown';
import { EditorView } from '@codemirror/view';
import FormTextInput from '../../sharedComponents/FormTextInput';

function PropertyValueEditor({ id, type, value, onChange, label }) {
  const handleEditorChange = (newValue) => {
    onChange({ target: { value: newValue } });
  };

  const getLanguageExtension = (type) => {
    switch (type) {
      case 'code': return javascript();
      case 'json': return json();
      case 'markdown': return [
        markdown(),
        EditorView.lineWrapping
      ];
      default: return javascript();
    }
  };

  // Safely parse/stringify JSON values
  const getEditorValue = (type, value) => {
    if (type === 'json') {
      try {
        return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
      } catch (e) {
        console.error('JSON parse error:', e);
        return '{}';
      }
    }
    return value || '';
  };

  const getEditorConfig = (type) => {
    const baseConfig = {
      dropCursor: true,
      allowMultipleSelections: false,
      indentOnInput: true,
    };

    switch (type) {
      case 'markdown':
        return {
          theme: 'light',
          style: { maxHeight: '600px', overflow: 'auto' },
          basicSetup: {
            ...baseConfig,
            lineNumbers: false,
            foldGutter: true,
            lineWrapping: true
          }
        };
      case 'code':
        return {
          theme: 'dark',
          style: { maxHeight: '400px', overflow: 'auto' },
          basicSetup: {
            ...baseConfig,
            lineNumbers: true,
            foldGutter: true,
          }
        };
      case 'json':
        return {
          theme: 'light',
          style: { maxHeight: '300px', overflow: 'auto' },
          basicSetup: {
            ...baseConfig,
            lineNumbers: true,
            foldGutter: false,
          }
        };
      default:
        return {
          theme: 'light',
          style: { maxHeight: '400px', overflow: 'auto' },
          basicSetup: baseConfig
        };
    }
  };

  if (['markdown', 'code', 'json'].includes(type)) {
    const editorConfig = getEditorConfig(type);
    return (
      <div className={`${type}-editor`} style={{ maxWidth: '100%' }}>
        <CodeMirror
          value={getEditorValue(type, value)}
          extensions={[getLanguageExtension(type)]}
          onChange={handleEditorChange}
          theme={editorConfig.theme}
          style={editorConfig.style}
          basicSetup={editorConfig.basicSetup}
        />
      </div>
    );
  }

  switch (type) {
    case 'file':
    case 'entity':
      return (
        <FormTextInput
          value={value}
          onChange={onChange}
          fullWidth
          label={label}
        />
      );

    default:
      return (
        <FormTextInput
          value={value}
          onChange={onChange}
          multiline
          minRows="1"
          fullWidth
          label={label}
        />
      );
  }
}

export default PropertyValueEditor;
